import { isEmpty } from 'lodash';
import { type ReactNode, useEffect, useState } from 'react';
import Button from '~/components2/forms/Button';
import Icon from '~/components2/Icon';
import { Notify } from '~/components2/Notify';
import rootStore from '~/store';

interface Props {
  selector?: string;
  position?: { top: number; left: number } | { bottom: number; right: number };
  positionOffset?: { top: number; left: number };
  nextLabel?: string;
  title?: string;
  width?: string;
  showSkip?: boolean;
  children: ReactNode;
  onNext?: () => void;
}

export default function OnboardingPopup({
  selector = '',
  title,
  position,
  children,
  nextLabel = 'Next',
  width = 'w-auto',
  showSkip = true,
  positionOffset = { top: 0, left: 0 },
  onNext,
}: Props) {
  const [el, setEl] = useState<Element | null>(null);

  useEffect(() => {
    if (isEmpty(selector)) {
      setEl(null);
      return;
    }
    const element = document.querySelector(selector);
    if (element) {
      setEl(element);
    } else {
      const observer = new MutationObserver((_mutationsList, observer) => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          setEl(element);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      // Clean up the observer on component unmount
      return () => observer.disconnect();
    }
  }, [selector]);

  if (selector) {
    if (!el) {
      // when el is not ready yet
      return null;
    } else {
      const rect = el.getBoundingClientRect();
      position = {
        left: rect.right + 10 + positionOffset.left,
        top: rect.y + positionOffset.top,
      };
    }
  }

  return (
    <div
      className={`z-50 flex ${position ? 'absolute' : 'fixed inset-0 items-center justify-center'}`}
      style={position}
    >
      <div className={`group rounded border bg-white p-6 shadow ${width}`}>
        {title && <div className="mb-1 text-base font-bold">{title}</div>}
        <div>{children}</div>
        <div className="mt-4 flex items-center justify-between gap-2 text-base">
          {showSkip ? (
            <Button
              alternate
              data-testid="onboarding-skip-button"
              className="invisible group-hover:visible"
              onClick={() => {
                Notify.info({
                  message: (
                    <div>
                      If you need assistance or want to take a product tour, click the
                      <span className="inline-block px-1">
                        <Icon icon="help2" />
                      </span>
                      icon in the top right corner.
                    </div>
                  ),
                  duration: 5_000,
                });
                rootStore.setInOnboardingMode(false);
              }}
            >
              Skip
            </Button>
          ) : (
            <div className="invisible" aria-hidden>
              dummy
            </div>
          )}
          {onNext ? (
            <Button data-testid="onboarding-next-button" onClick={onNext}>
              {nextLabel}
            </Button>
          ) : (
            <div className="invisible" aria-hidden>
              dummy
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
